import React, { useEffect, useState, useRef } from 'react';
import { ZoomInIcon, ZoomOutIcon, MoveIcon } from 'lucide-react';
interface DrawingCanvasProps {
  activeTab: string;
  measurements: any[];
  onAddWall: (wall: any) => void;
}
export function DrawingCanvas({
  activeTab,
  measurements,
  onAddWall
}: DrawingCanvasProps) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPoint, setCurrentPoint] = useState(null);
  const [scale, setScale] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx, canvas.width, canvas.height);
    }
    // Draw existing measurements
    drawMeasurements(ctx);
    // Draw current line if drawing
    if (isDrawing && startPoint && currentPoint) {
      ctx.beginPath();
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.strokeStyle = '#3B82F6';
      ctx.lineWidth = 2;
      ctx.stroke();
      // Draw distance
      const distance = calculateDistance(startPoint, currentPoint);
      const midPoint = {
        x: (startPoint.x + currentPoint.x) / 2,
        y: (startPoint.y + currentPoint.y) / 2
      };
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(midPoint.x - 30, midPoint.y - 10, 60, 20);
      ctx.fillStyle = '#3B82F6';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(distance)} cm`, midPoint.x, midPoint.y + 4);
    }
  }, [measurements, isDrawing, startPoint, currentPoint, showGrid, scale]);
  const drawGrid = (ctx, width, height) => {
    const gridSize = 20 * scale;
    ctx.beginPath();
    ctx.strokeStyle = '#E5E7EB';
    // Vertical lines
    for (let x = 0; x < width; x += gridSize) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    // Horizontal lines
    for (let y = 0; y < height; y += gridSize) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();
  };
  const drawMeasurements = ctx => {
    measurements.forEach(wall => {
      // Draw wall
      ctx.beginPath();
      ctx.moveTo(wall.start.x, wall.start.y);
      ctx.lineTo(wall.end.x, wall.end.y);
      ctx.strokeStyle = '#2563EB';
      ctx.lineWidth = 3;
      ctx.stroke();
      // Draw points
      ctx.fillStyle = '#2563EB';
      ctx.beginPath();
      ctx.arc(wall.start.x, wall.start.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(wall.end.x, wall.end.y, 5, 0, Math.PI * 2);
      ctx.fill();
      // Draw measurement
      const midPoint = {
        x: (wall.start.x + wall.end.x) / 2,
        y: (wall.start.y + wall.end.y) / 2
      };
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(midPoint.x - 30, midPoint.y - 10, 60, 20);
      ctx.fillStyle = '#2563EB';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${wall.length} cm`, midPoint.x, midPoint.y + 4);
    });
  };
  const calculateDistance = (point1, point2) => {
    const pixelDistance = Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2));
    // Convert to cm (assuming 100 pixels = 100 cm at scale 1)
    return pixelDistance / scale;
  };
  const handleMouseDown = e => {
    if (activeTab !== 'draw') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setIsDrawing(true);
    setStartPoint({
      x,
      y
    });
    setCurrentPoint({
      x,
      y
    });
  };
  const handleMouseMove = e => {
    if (!isDrawing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCurrentPoint({
      x,
      y
    });
  };
  const handleMouseUp = () => {
    if (!isDrawing) return;
    // Add wall to measurements
    if (startPoint && currentPoint) {
      const distance = calculateDistance(startPoint, currentPoint);
      if (distance > 10) {
        // Minimum wall length
        onAddWall({
          start: startPoint,
          end: currentPoint,
          length: Math.round(distance)
        });
      }
    }
    setIsDrawing(false);
  };
  const zoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 3));
  };
  const zoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.5));
  };
  return <div className="w-full h-full bg-gray-100 relative" data-id="element-136">
      <canvas ref={canvasRef} className="w-full h-full" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-137"></canvas>
      {/* Canvas tools */}
      <div className="absolute bottom-4 right-4 bg-white rounded-md shadow-md p-1 flex" data-id="element-138">
        <button onClick={zoomIn} className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded" data-id="element-139">
          <ZoomInIcon className="h-5 w-5" data-id="element-140" />
        </button>
        <button onClick={zoomOut} className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded" data-id="element-141">
          <ZoomOutIcon className="h-5 w-5" data-id="element-142" />
        </button>
        <button className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded" data-id="element-143">
          <MoveIcon className="h-5 w-5" data-id="element-144" />
        </button>
        <button className="p-2 text-gray-700 hover:bg-gray-100 hover:text-blue-600 rounded" data-id="element-145">
          <div className="h-5 w-5" data-id="element-146" />
        </button>
        <button onClick={() => setShowGrid(!showGrid)} className={`p-2 rounded ${showGrid ? 'bg-blue-100 text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-147">
          <div className="h-5 w-5" data-id="element-148" />
        </button>
      </div>
      {/* Instructions */}
      {activeTab === 'draw' && measurements.length === 0 && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center bg-white bg-opacity-90 p-4 rounded-lg shadow-md" data-id="element-149">
          <p className="text-lg font-medium text-gray-700 mb-2" data-id="element-150">
            Click on the canvas to start drawing your room
          </p>
          <p className="text-sm text-gray-500" data-id="element-151">
            Draw walls in sequence to create the room outline
          </p>
        </div>}
      {/* Measurements */}
      <div className="absolute bottom-4 left-4 bg-white rounded-md shadow-md p-2" data-id="element-152">
        <div className="text-xs font-medium text-gray-700" data-id="element-153">
          <span data-id="element-154">Scale: 1:{Math.round(100 / scale)}</span>
          {measurements.length > 0 && <>
              <span className="mx-2" data-id="element-155">|</span>
              <span data-id="element-156">Walls: {measurements.length}</span>
            </>}
        </div>
      </div>
    </div>;
}